import { isAxiosError } from "axios";

export const CHAT_DOCUMENT_LIMIT_ERROR_CODE = "chat_document_limit_exceeded";

export const CHAT_DOCUMENT_LIMIT_MESSAGE =
    "Couldn't attach — over the document limit";

export interface ChatDocumentLimitDetail {
    code: typeof CHAT_DOCUMENT_LIMIT_ERROR_CODE;
    limit: number;
}

/** Thrown by the adapters so the limit rejection stays typed past the catch. */
export class ChatDocumentLimitExceededError extends Error {
    readonly code = CHAT_DOCUMENT_LIMIT_ERROR_CODE;
    readonly limit: number;

    constructor(limit: number) {
        super(CHAT_DOCUMENT_LIMIT_MESSAGE);
        this.name = "ChatDocumentLimitExceededError";
        this.limit = limit;
    }
}

function isLimitDetail(detail: unknown): detail is ChatDocumentLimitDetail {
    if (typeof detail !== "object" || detail === null) return false;
    const candidate = detail as Partial<ChatDocumentLimitDetail>;
    return (
        candidate.code === CHAT_DOCUMENT_LIMIT_ERROR_CODE &&
        typeof candidate.limit === "number" &&
        Number.isFinite(candidate.limit)
    );
}

async function decodeResponseData(data: unknown): Promise<unknown> {
    if (
        typeof ReadableStream === "undefined" ||
        !(data instanceof ReadableStream)
    ) {
        return data;
    }
    try {
        return await new Response(data).json();
    } catch {
        return null;
    }
}

/**
 * Recover the typed document-limit rejection, or `null` for anything else.
 *
 * `getApiErrorMessage` cannot carry this: it returns `detail` only when it is a
 * string and otherwise falls through to a generic fallback, so a `detail`
 * object degrades into "An error occurred" and the limit is lost. This reads
 * the object form instead of replacing that helper, which every other caller
 * still relies on.
 *
 * 401s are excluded on purpose. Those belong to the auth interceptor's
 * re-authentication path, and a limit rejection is never one.
 */
export async function extractChatDocumentLimitError(
    error: unknown
): Promise<ChatDocumentLimitDetail | null> {
    if (error instanceof ChatDocumentLimitExceededError) {
        return { code: error.code, limit: error.limit };
    }
    if (!isAxiosError(error)) return null;
    if (error.response?.status === 401) return null;

    const responseData = await decodeResponseData(error.response?.data);
    const detail = (responseData as { detail?: unknown } | undefined)?.detail;
    return isLimitDetail(detail) ? detail : null;
}
